import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import { Button } from "../components/ui/button";
import { 
    ArrowRight, Shield, Globe, Activity, Building2, 
    Landmark, Radio, Briefcase, ChevronRight, Play, 
    Server, Zap, MapPin, Database, Lock, Cpu, CheckCircle2, Key,
    Cloud, Brain, RefreshCw, Gauge, ChevronLeft
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useConsultationModal } from '../context/ConsultationModalContext';
import { useTheme } from '../context/ThemeContext';
import RoutingLogic from '../components/RoutingLogic';
import PerformanceInsights from '../components/PerformanceInsights';

// Slide data
const heroSlides = [
    {
        id: 1,
        badge: "SCALABLE. RELIABLE. NEPALI.",
        title: "Nepal's Sovereign",
        highlight: "Enterprise Cloud",
        description: "Built exclusively for banks, government, and regulated enterprises. Absolute data sovereignty, defense-grade security, and AI-ready performance — all within Nepal.",
        icon: Cloud,
        accentColor: "from-accent to-yellow-600",
        particles: "accent"
    },
    {
        id: 2,
        badge: "AI-READY INFRASTRUCTURE",
        title: "Built for Modern",
        highlight: "AI & Analytics",
        description: "Deploy training and inference workloads on high-performance GPU infrastructure without redesigning your environment.",
        icon: Brain,
        accentColor: "from-purple-500 to-blue-600",
        particles: "purple"
    },
    {
        id: 3,
        badge: "99.99% UPTIME GUARANTEED",
        title: "Always On.",
        highlight: "Even When It Matters Most.",
        description: "Multi-site architecture with built-in disaster recovery ensures your systems stay available — even during outages or disasters.",
        icon: RefreshCw,
        accentColor: "from-emerald-500 to-teal-600",
        particles: "emerald"
    },
    {
        id: 4,
        badge: "ENTERPRISE PERFORMANCE",
        title: "Dedicated Resources.",
        highlight: "Predictable Performance.",
        description: "No noisy neighbors. No oversubscription. Just consistent compute, storage, and network performance for demanding workloads.",
        icon: Gauge,
        accentColor: "from-orange-500 to-red-600",
        particles: "orange"
    }
];

const Home = () => {
  const { openModal } = useConsultationModal();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState('next');
  
  // Auto-advance slides
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isAnimating) {
        setDirection('next');
        setIsAnimating(true);
        setTimeout(() => {
          setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
          setIsAnimating(false);
        }, 500);
      }
    }, 6000);
    return () => clearInterval(interval);
  }, [isAnimating]);

  const goToSlide = (index) => {
    if (isAnimating || index === currentSlide) return;
    setDirection(index > currentSlide ? 'next' : 'prev');
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentSlide(index);
      setIsAnimating(false);
    }, 500);
  };

  const nextSlide = () => {
    if (isAnimating) return;
    setDirection('next');
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
      setIsAnimating(false);
    }, 500);
  };

  const prevSlide = () => {
    if (isAnimating) return;
    setDirection('prev');
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
      setIsAnimating(false);
    }, 500);
  };

  const slide = heroSlides[currentSlide];

  return (
    <Layout>
      <SEO page="home" />
      {/* 1. HERO SECTION - Animated Slider */}
      <section className="relative min-h-screen flex items-start md:items-center justify-center overflow-hidden pt-32 md:pt-28 pb-24">
        
        {/* Background Image */}
        <div 
            className="absolute inset-0 bg-cover bg-no-repeat transition-transform duration-1000"
            style={{ 
                backgroundImage: "url('https://customer-assets.emergentagent.com/job_everestcloud/artifacts/spue5mbw_backgroundjpeg.jpg')",
                backgroundPosition: "center 30%",
                transform: isAnimating ? 'scale(1.05)' : 'scale(1)',
            }}
        ></div>
        
        {/* Dynamic Gradient Overlay based on slide */}
        <div className={`absolute inset-0 transition-all duration-1000 ${
          slide.particles === 'accent' ? 'bg-gradient-to-b from-white/60 via-white/80 to-white dark:from-slate-950/60 dark:via-slate-950/80 dark:to-slate-950' :
          slide.particles === 'purple' ? 'bg-gradient-to-b from-purple-50/60 via-white/80 to-white dark:from-purple-950/40 dark:via-slate-950/80 dark:to-slate-950' :
          slide.particles === 'emerald' ? 'bg-gradient-to-b from-emerald-50/60 via-white/80 to-white dark:from-emerald-950/40 dark:via-slate-950/80 dark:to-slate-950' :
          'bg-gradient-to-b from-orange-50/60 via-white/80 to-white dark:from-orange-950/40 dark:via-slate-950/80 dark:to-slate-950'
        }`}></div>

        {/* AI Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.08] dark:opacity-[0.12] pointer-events-none"
             style={{
                 backgroundImage: `
                     linear-gradient(${slide.particles === 'accent' ? 'rgba(234,179,8,0.4)' : 
                       slide.particles === 'purple' ? 'rgba(168,85,247,0.4)' :
                       slide.particles === 'emerald' ? 'rgba(16,185,129,0.4)' : 'rgba(249,115,22,0.4)'} 1px, transparent 1px),
                     linear-gradient(90deg, ${slide.particles === 'accent' ? 'rgba(234,179,8,0.4)' : 
                       slide.particles === 'purple' ? 'rgba(168,85,247,0.4)' :
                       slide.particles === 'emerald' ? 'rgba(16,185,129,0.4)' : 'rgba(249,115,22,0.4)'} 1px, transparent 1px)
                 `,
                 backgroundSize: '60px 60px',
                 maskImage: 'radial-gradient(ellipse at center, black 40%, transparent 75%)'
             }}>
        </div>

        {/* Animated Particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(12)].map((_, i) => (
                <div
                    key={i}
                    className={`absolute rounded-full transition-all duration-1000 ${
                        slide.particles === 'accent' ? 'bg-accent/60' :
                        slide.particles === 'purple' ? 'bg-purple-500/60' :
                        slide.particles === 'emerald' ? 'bg-emerald-500/60' : 'bg-orange-500/60'
                    }`}
                    style={{
                        width: `${8 + (i % 4) * 4}px`,
                        height: `${8 + (i % 4) * 4}px`,
                        top: `${10 + (i * 7) % 70}%`,
                        left: `${5 + (i * 11) % 85}%`,
                        animation: `float-${['slow', 'medium', 'fast'][i % 3]} ${3 + i % 3}s ease-in-out infinite`,
                        animationDelay: `${i * 0.2}s`,
                        boxShadow: `0 0 ${10 + i * 2}px ${
                            slide.particles === 'accent' ? 'rgba(234,179,8,0.5)' :
                            slide.particles === 'purple' ? 'rgba(168,85,247,0.5)' :
                            slide.particles === 'emerald' ? 'rgba(16,185,129,0.5)' : 'rgba(249,115,22,0.5)'
                        }`
                    }}
                />
            ))}
        </div>

        {/* Glowing Orbs */}
        <div className={`absolute top-[10%] left-[15%] w-96 h-96 rounded-full blur-[100px] animate-pulse-slow pointer-events-none transition-all duration-1000 ${
            slide.particles === 'accent' ? 'bg-accent/25' :
            slide.particles === 'purple' ? 'bg-purple-500/25' :
            slide.particles === 'emerald' ? 'bg-emerald-500/25' : 'bg-orange-500/25'
        }`}></div>
        <div className={`absolute bottom-[15%] right-[10%] w-80 h-80 rounded-full blur-[80px] animate-pulse-slower pointer-events-none transition-all duration-1000 ${
            slide.particles === 'accent' ? 'bg-blue-500/20' :
            slide.particles === 'purple' ? 'bg-blue-500/20' :
            slide.particles === 'emerald' ? 'bg-teal-500/20' : 'bg-red-500/20'
        }`}></div>

        {/* Large Floating Icon Background */}
        <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transition-all duration-700 ${isAnimating ? 'opacity-0 scale-75' : 'opacity-[0.03] dark:opacity-[0.05] scale-100'}`}>
            <slide.icon className="w-[600px] h-[600px]" strokeWidth={0.5} />
        </div>

        {/* Main Content */}
        <div className="container relative z-10 mx-auto px-4 flex flex-col items-center text-center">
          
            {/* Tagline Badge */}
            <div className={`inline-flex items-center gap-2 px-5 py-2 rounded-full border mb-8 backdrop-blur-sm transition-all duration-500 ${
                isAnimating ? 'opacity-0 -translate-y-4' : 'opacity-100 translate-y-0'
            } ${
                slide.particles === 'accent' ? 'bg-accent/10 border-accent/20' :
                slide.particles === 'purple' ? 'bg-purple-500/10 border-purple-500/20' :
                slide.particles === 'emerald' ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-orange-500/10 border-orange-500/20'
            }`}>
                <span className={`flex h-2 w-2 rounded-full animate-pulse ${
                    slide.particles === 'accent' ? 'bg-accent' :
                    slide.particles === 'purple' ? 'bg-purple-500' :
                    slide.particles === 'emerald' ? 'bg-emerald-500' : 'bg-orange-500'
                }`}></span>
                <span className={`text-xs font-bold tracking-[0.15em] uppercase ${
                    slide.particles === 'accent' ? 'text-accent' :
                    slide.particles === 'purple' ? 'text-purple-600 dark:text-purple-400' :
                    slide.particles === 'emerald' ? 'text-emerald-600 dark:text-emerald-400' : 'text-orange-600 dark:text-orange-400'
                }`}>{slide.badge}</span>
            </div>
            
            {/* Headline */}
            <h1 className={`text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight text-slate-900 dark:text-white mb-6 leading-[1.1] transition-all duration-500 ${
                isAnimating ? 'opacity-0 translate-y-8' : 'opacity-100 translate-y-0'
            }`}>
                {slide.title} <br/>
                <span className={`text-transparent bg-clip-text bg-gradient-to-r ${slide.accentColor}`}>{slide.highlight}</span>
            </h1>
            
            {/* Sub-headline */}
            <p className={`text-lg md:text-xl text-slate-600 dark:text-slate-300 mb-10 max-w-3xl mx-auto leading-relaxed font-normal transition-all duration-500 delay-100 ${
                isAnimating ? 'opacity-0 translate-y-8' : 'opacity-100 translate-y-0'
            }`}>
                {slide.description}
            </p>
            
            {/* CTAs */}
            <div className={`flex flex-col sm:flex-row gap-4 justify-center transition-all duration-500 delay-150 ${
                isAnimating ? 'opacity-0 translate-y-8' : 'opacity-100 translate-y-0'
            }`}>
                <Button 
                    size="xl" 
                    className={`h-14 px-10 text-lg font-bold transition-all rounded-full shadow-lg hover:shadow-xl hover:scale-105 ${
                        slide.particles === 'accent' ? 'bg-accent text-black hover:bg-accent/90' :
                        slide.particles === 'purple' ? 'bg-purple-600 text-white hover:bg-purple-700' :
                        slide.particles === 'emerald' ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-orange-600 text-white hover:bg-orange-700'
                    }`}
                    onClick={() => openModal('demo')}
                >
                    Request Demo
                </Button>
                <Button size="xl" variant="outline" className="h-14 px-10 text-lg font-medium border-slate-300 dark:border-white/20 bg-white/50 dark:bg-white/5 hover:bg-white dark:hover:bg-white/10 text-slate-900 dark:text-white rounded-full transition-all backdrop-blur-sm" asChild>
                    <Link to="/platform">View Platform</Link>
                </Button>
            </div>

            {/* Slide Indicators */}
            <div className="flex items-center gap-3 mt-12">
                {heroSlides.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => goToSlide(index)}
                        className={`relative h-2 rounded-full transition-all duration-500 ${
                            index === currentSlide ? 'w-12' : 'w-2 hover:w-4'
                        } ${
                            index === currentSlide ? (
                                slide.particles === 'accent' ? 'bg-accent' :
                                slide.particles === 'purple' ? 'bg-purple-500' :
                                slide.particles === 'emerald' ? 'bg-emerald-500' : 'bg-orange-500'
                            ) : 'bg-slate-300 dark:bg-slate-600 hover:bg-slate-400 dark:hover:bg-slate-500'
                        }`}
                        aria-label={`Go to slide ${index + 1}`}
                    >
                        {index === currentSlide && (
                            <span className={`absolute inset-0 rounded-full animate-pulse ${
                                slide.particles === 'accent' ? 'bg-accent/50' :
                                slide.particles === 'purple' ? 'bg-purple-500/50' :
                                slide.particles === 'emerald' ? 'bg-emerald-500/50' : 'bg-orange-500/50'
                            }`}></span>
                        )}
                    </button>
                ))}
            </div>

            {/* Nutanix Verified Badges - Hero Section (Fixed, no animation) */}
            <div className="mt-12">
                <div className="flex items-center justify-center gap-5 md:gap-10">
                    <img 
                        src="https://customer-assets-39nsmqrw.emergentagent.net/job_90cab74e-6446-4874-960d-502f68693f2e/artifacts/lmjcipv6_ServiceProvider-BadgingSystem-FY26Q2-VerifiedSolution-SovereignCloud.png"
                        alt="Powered by Nutanix — Verified Solution: Sovereign Cloud"
                        className="h-24 md:h-28 lg:h-32 w-auto object-contain drop-shadow-lg"
                    />
                    <img 
                        src="https://customer-assets-39nsmqrw.emergentagent.net/job_90cab74e-6446-4874-960d-502f68693f2e/artifacts/9hszlx5a_ServiceProvider-BadgingSystem-FY26Q2-VerifiedSolution-DedicatedPrivateCloud.png"
                        alt="Powered by Nutanix — Verified Solution: Dedicated Private Cloud"
                        className="h-24 md:h-28 lg:h-32 w-auto object-contain drop-shadow-lg"
                    />
                    <img 
                        src="https://customer-assets-39nsmqrw.emergentagent.net/job_90cab74e-6446-4874-960d-502f68693f2e/artifacts/jge5p3sx_ServiceProvider-BadgingSystem-FY26Q2-VerifiedSolution-Multi-Tenant.png"
                        alt="Powered by Nutanix — Verified Solution: Multi-Tenant Private Cloud"
                        className="h-24 md:h-28 lg:h-32 w-auto object-contain drop-shadow-lg"
                    />
                    <img 
                        src="https://customer-assets-39nsmqrw.emergentagent.net/job_90cab74e-6446-4874-960d-502f68693f2e/artifacts/7b6panhm_ServiceProvider-BadgingSystem-FY26Q2-VerifiedSolution-DRaaService.png"
                        alt="Powered by Nutanix — Verified Solution: DRaaS"
                        className="h-24 md:h-28 lg:h-32 w-auto object-contain drop-shadow-lg"
                    />
                </div>
            </div>

        </div>

        {/* Navigation Arrows */}
        <button
            onClick={prevSlide}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm border border-slate-200 dark:border-white/10 shadow-lg hover:scale-110 transition-all opacity-60 hover:opacity-100"
            aria-label="Previous slide"
        >
            <ChevronLeft className="w-6 h-6 text-slate-700 dark:text-white" />
        </button>
        <button
            onClick={nextSlide}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm border border-slate-200 dark:border-white/10 shadow-lg hover:scale-110 transition-all opacity-60 hover:opacity-100"
            aria-label="Next slide"
        >
            <ChevronRight className="w-6 h-6 text-slate-700 dark:text-white" />
        </button>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce z-20">
            <div className="w-7 h-11 border-2 border-slate-900/40 dark:border-white/60 rounded-full flex justify-center pt-2 bg-white/50 dark:bg-black/30 backdrop-blur-sm shadow-lg">
                <div className="w-1.5 h-3 bg-slate-900/60 dark:bg-white/80 rounded-full animate-pulse"></div>
            </div>
        </div>
      </section>

      {/* 2. TRUSTED SECTORS */}
      <section className="py-24 bg-white dark:bg-slate-950 border-y border-slate-200 dark:border-white/5">
        <div className="container mx-auto px-4">
            <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">Trusted by Nepal’s Most Critical Sectors</h2>
                <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
                    EverestCloud is purpose-built for organizations where downtime, data loss, or regulatory failure is unacceptable.
                </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
               <SectorCard 
                    icon={<Landmark />} 
                    title="Banking & BFSI" 
                    desc="Core banking, DR, compliance, analytics" 
                />
               <SectorCard 
                    icon={<Building2 />} 
                    title="Government & PSU" 
                    desc="Citizen data, national systems" 
                />
               <SectorCard 
                    icon={<Radio />} 
                    title="Telecom & ISPs" 
                    desc="OSS/BSS, low latency workloads" 
                />
               <SectorCard 
                    icon={<Briefcase />} 
                    title="Large Enterprises" 
                    desc="ERP, mission-critical platforms" 
                />
            </div>
        </div>
      </section>

      {/* VERIFIED BY NUTANIX - TRUST BADGES - ALWAYS NAVY BACKGROUND */}
      <section className="py-20 bg-navy-force">
          <div className="container mx-auto px-4">
              <div className="text-center mb-12">
                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                      Nepal's First <span className="text-amber-500">Nutanix-Verified</span> Sovereign Cloud
                  </h2>
                  <p className="text-lg text-slate-300 max-w-3xl mx-auto">
                      Verified Solution in all four categories — Sovereign Cloud · Dedicated Private Cloud · Multi-Tenant Private Cloud · DRaaS (2026).
                  </p>
              </div>
              
              {/* Badges Grid - 4 across desktop, 2x2 mobile */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 max-w-5xl mx-auto">
                  <div className="flex items-center justify-center">
                      <img 
                          src="https://customer-assets-39nsmqrw.emergentagent.net/job_90cab74e-6446-4874-960d-502f68693f2e/artifacts/lmjcipv6_ServiceProvider-BadgingSystem-FY26Q2-VerifiedSolution-SovereignCloud.png"
                          alt="Powered by Nutanix — Verified Solution: Sovereign Cloud"
                          className="h-28 md:h-32 lg:h-36 w-auto object-contain"
                      />
                  </div>
                  <div className="flex items-center justify-center">
                      <img 
                          src="https://customer-assets-39nsmqrw.emergentagent.net/job_90cab74e-6446-4874-960d-502f68693f2e/artifacts/9hszlx5a_ServiceProvider-BadgingSystem-FY26Q2-VerifiedSolution-DedicatedPrivateCloud.png"
                          alt="Powered by Nutanix — Verified Solution: Dedicated Private Cloud"
                          className="h-28 md:h-32 lg:h-36 w-auto object-contain"
                      />
                  </div>
                  <div className="flex items-center justify-center">
                      <img 
                          src="https://customer-assets-39nsmqrw.emergentagent.net/job_90cab74e-6446-4874-960d-502f68693f2e/artifacts/jge5p3sx_ServiceProvider-BadgingSystem-FY26Q2-VerifiedSolution-Multi-Tenant.png"
                          alt="Powered by Nutanix — Verified Solution: Multi-Tenant Private Cloud"
                          className="h-28 md:h-32 lg:h-36 w-auto object-contain"
                      />
                  </div>
                  <div className="flex items-center justify-center">
                      <img 
                          src="https://customer-assets-39nsmqrw.emergentagent.net/job_90cab74e-6446-4874-960d-502f68693f2e/artifacts/7b6panhm_ServiceProvider-BadgingSystem-FY26Q2-VerifiedSolution-DRaaService.png"
                          alt="Powered by Nutanix — Verified Solution: DRaaS"
                          className="h-28 md:h-32 lg:h-36 w-auto object-contain"
                      />
                  </div>
              </div>
          </div>
      </section>

      {/* MOVED UP: AI & GPU CLOUD */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950 border-b border-slate-200 dark:border-white/5">
        <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-6">Nepal’s First Enterprise-Grade AI Cloud</h2>
            <p className="text-xl text-slate-600 dark:text-slate-400 max-w-3xl mx-auto mb-12">
                Position your organization for the future with secure, high-performance AI infrastructure.
            </p>
            
            <div className="max-w-5xl mx-auto bg-white dark:bg-slate-950 border border-slate-200 dark:border-white/10 rounded-3xl p-8 md:p-12 relative overflow-hidden group hover:border-accent/30 transition-colors shadow-2xl dark:shadow-none">
                <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-5"></div>
                <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/10 blur-[100px] rounded-full"></div>
                
                <div className="grid md:grid-cols-2 gap-12 relative z-10 text-left">
                    <div>
                        <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">High Performance Compute</h3>
                        <ul className="space-y-3">
                            <li className="flex items-start gap-3 text-slate-600 dark:text-slate-300">
                                <Zap className="h-5 w-5 text-purple-500 shrink-0 mt-1" />
                                <span>NVIDIA H100 / RTX Pro Blackwell Servers</span>
                            </li>
                            <li className="flex items-start gap-3 text-slate-600 dark:text-slate-300">
                                <Zap className="h-5 w-5 text-purple-500 shrink-0 mt-1" />
                                <span>L4 GPU Clusters for Inference</span>
                            </li>
                            <li className="flex items-start gap-3 text-slate-600 dark:text-slate-300">
                                <Zap className="h-5 w-5 text-purple-500 shrink-0 mt-1" />
                                <span>Ultra-low latency training</span>
                            </li>
                        </ul>
                    </div>
                    <div>
                        <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">Secure AI Workloads</h3>
                        <p className="text-slate-600 dark:text-slate-400 mb-4">
                            Ideal for BFSI risk modeling, fraud detection, and regulated analytics workloads where data privacy is paramount.
                        </p>
                        <div className="inline-block px-4 py-2 bg-purple-500/10 rounded-lg border border-purple-500/20 text-purple-600 dark:text-purple-300 text-sm font-medium">
                            Secure Isolated Tenancy
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Local Peering Animation */}
            <RoutingLogic isDarkMode={isDarkMode} />
            
            {/* Performance Insights */}
            <PerformanceInsights isDarkMode={isDarkMode} />
        </div>
      </section>

      {/* 3. WHY SOVEREIGN CLOUD MATTERS */}
      <section className="py-24 bg-slate-50 dark:bg-slate-900 relative overflow-hidden">
         <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-blue-500/5 to-transparent"></div>
         <div className="container mx-auto px-4 relative z-10">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
                <div>
                    <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-8 leading-tight">
                        Why Sovereign Cloud Is <br/>
                        <span className="text-accent">No Longer Optional</span>
                    </h2>
                    <p className="text-xl text-slate-600 dark:text-slate-300 mb-8 leading-relaxed">
                        In an era of geopolitical uncertainty and strict data localization laws, relying on global public clouds introduces unacceptable risks.
                    </p>
                    <div className="space-y-6">
                        <RiskSolution 
                            risk="Jurisdictional Risk" 
                            solution="Data never leaves Nepal. Fully aligned with local laws." 
                        />
                        <RiskSolution 
                            risk="Regulatory Uncertainty" 
                            solution="Compliant with NRB directives and local regulations." 
                        />
                        <RiskSolution 
                            risk="Data Exposure" 
                            solution="Local legal jurisdiction and full auditability." 
                        />
                    </div>
                </div>
                <div className="bg-white dark:bg-slate-800/50 p-10 rounded-2xl border border-slate-200 dark:border-white/5 backdrop-blur-sm relative shadow-xl dark:shadow-none">
                    <div className="absolute -inset-1 bg-gradient-to-r from-accent/20 to-blue-500/20 rounded-2xl blur opacity-30"></div>
                    <Globe className="h-20 w-20 text-accent mb-6" />
                    <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">The Sovereign Guarantee</h3>
                    <p className="text-slate-600 dark:text-slate-400 text-lg leading-relaxed">
                        &ldquo;Your data remains physically and legally within the borders of Nepal, protected by our laws and accessible only to you.&rdquo;
                    </p>
                </div>
            </div>
         </div>
      </section>

      {/* 4. PLATFORM OVERVIEW */}
      <section className="py-24 bg-white dark:bg-slate-950">
        <div className="container mx-auto px-4">
            <div className="text-center mb-20">
                <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-6">Enterprise Cloud. Engineered for Nepal.</h2>
                <p className="text-xl text-slate-600 dark:text-slate-400 max-w-3xl mx-auto">
                    A comprehensive, full-stack enterprise platform designed for performance and security.
                </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
                <FeatureBlock 
                    icon={<Cpu />} 
                    title="Compute & Storage" 
                    desc="High-performance compute & NVMe storage for demanding apps."
                />
                <FeatureBlock 
                    icon={<Shield />} 
                    title="Network Security" 
                    desc="Secure virtual networking & micro-segmentation by default."
                />
                <FeatureBlock 
                    icon={<Database />} 
                    title="Data Protection" 
                    desc="Built-in Disaster Recovery and automated backup services."
                />
                <FeatureBlock 
                    icon={<Activity />} 
                    title="Managed SOC" 
                    desc="24/7 Security Operations Center and compliance monitoring."
                />
                <FeatureBlock 
                    icon={<Zap />} 
                    title="AI Infrastructure" 
                    desc="GPU-powered AI/ML infrastructure for next-gen workloads."
                />
                <FeatureBlock 
                    icon={<Lock />} 
                    title="Compliance Ready" 
                    desc="Pre-configured controls for BFSI and government audit readiness."
                />
            </div>
        </div>
      </section>

      {/* 5. ARCHITECTURE & RESILIENCE - CINEMATIC & CONNECTED (COMPACT & THEME ADAPTIVE) */}
      <section className="py-24 bg-slate-50 dark:bg-[#020617] border-t border-slate-200 dark:border-white/5 relative overflow-hidden transition-colors duration-500">
        
        {/* SVG Connection Layer - CONNECTING THE BOXES */}
        <div className="absolute inset-0 pointer-events-none z-0 hidden md:block">
            <svg className="w-full h-full" preserveAspectRatio="none">
                <defs>
                    <linearGradient id="syncFlowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="transparent" />
                        <stop offset="50%" stopColor="#eab308" />
                        <stop offset="100%" stopColor="transparent" />
                    </linearGradient>
                    <linearGradient id="asyncFlowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="transparent" />
                        <stop offset="50%" stopColor="#3b82f6" />
                        <stop offset="100%" stopColor="transparent" />
                    </linearGradient>
                </defs>
                
                {/* Connection Line: KTM (Col 1) <-> LAL (Col 2) */}
                <path 
                    d="M 17% 55% L 50% 55%" 
                    stroke="currentColor"
                    className="text-slate-300 dark:text-white/10"
                    strokeWidth="3" 
                    strokeDasharray="6 6" 
                    fill="none" 
                />
                
                {/* Connection Line: LAL (Col 2) -> BTL (Col 3) */}
                <path 
                    d="M 50% 55% L 83% 55%" 
                    stroke="currentColor"
                    className="text-slate-300 dark:text-white/10" 
                    strokeWidth="3" 
                    strokeDasharray="6 6" 
                    fill="none" 
                />

                {/* Animated Packets: KTM <-> LAL (Sync - Rapid) */}
                <circle r="6" fill="#eab308" filter="url(#glow)">
                    <animateMotion dur="1.5s" repeatCount="indefinite" path="M 17% 55% L 50% 55%" />
                </circle>
                <circle r="6" fill="#eab308" filter="url(#glow)">
                    <animateMotion dur="1.5s" repeatCount="indefinite" path="M 50% 55% L 17% 55%" />
                </circle>

                {/* Animated Packets: LAL -> BTL (Async - Slower) */}
                <circle r="6" fill="#3b82f6" filter="url(#glow)">
                    <animateMotion dur="3s" repeatCount="indefinite" path="M 50% 55% L 83% 55%" />
                </circle>

                {/* Filters for Glow */}
                <filter id="glow">
                    <feGaussianBlur stdDeviation="2.5" result="coloredBlur"/>
                    <feMerge>
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                </filter>
            </svg>
        </div>

        <div className="container mx-auto px-4 relative z-10">
            <div className="mb-12 text-center">
                <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4 tracking-tight">Designed for Business Continuity</h2>
                <p className="text-xl text-slate-600 dark:text-accent/80 font-medium tracking-wide">Even During Disasters.</p>
            </div>

            {/* Grid Layout for Cards - Compact & Theme Adaptive */}
            <div className="grid md:grid-cols-3 gap-6 lg:gap-8 relative">
                
                {/* Kathmandu */}
                <div className="relative group">
                    {/* Connection Point Indicator */}
                    <div className="hidden md:block absolute right-[-12px] lg:right-[-16px] top-1/2 -translate-y-1/2 w-4 h-4 bg-accent rounded-full shadow-[0_0_15px_#eab308] z-20 animate-pulse border-2 border-white dark:border-slate-900"></div>
                    
                    <div className="bg-white dark:bg-slate-900/90 border border-slate-200 dark:border-accent/30 p-5 rounded-xl h-full shadow-lg dark:shadow-[0_0_30px_rgba(234,179,8,0.15)] hover:shadow-xl dark:hover:shadow-[0_0_50px_rgba(234,179,8,0.25)] transition-all duration-500 relative z-10 backdrop-blur-xl">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-ping"></div>
                            <span className="text-[10px] font-bold text-green-600 dark:text-green-400 uppercase tracking-widest">Active</span>
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-1 uppercase tracking-wide">KATHMANDU (AZ-1)</h3>
                        <p className="text-accent text-xs font-bold mb-3 uppercase tracking-wider">Primary DC</p>
                        <ul className="space-y-1.5">
                            <ZoneBullet text="<1ms Local Latency (NPIX)" />
                            <ZoneBullet text="99.9% Uptime SLA Guaranteed" />
                            <ZoneBullet text="Carrier Neutral Connectivity" />
                            <ZoneBullet text="Tier III Aligned Facility" />
                            <ZoneBullet text="24/7 Physical & Cyber Security" />
                        </ul>
                    </div>
                </div>

                {/* Lalitpur */}
                <div className="relative group">
                    {/* Connection Points */}
                    <div className="hidden md:block absolute left-[-12px] lg:left-[-16px] top-1/2 -translate-y-1/2 w-4 h-4 bg-accent rounded-full shadow-[0_0_15px_#eab308] z-20 border-2 border-white dark:border-slate-900"></div>
                    <div className="hidden md:block absolute right-[-12px] lg:right-[-16px] top-1/2 -translate-y-1/2 w-4 h-4 bg-blue-500 rounded-full shadow-[0_0_15px_#3b82f6] z-20 border-2 border-white dark:border-slate-900"></div>

                    <div className="bg-white dark:bg-slate-900/90 border border-slate-200 dark:border-white/10 p-5 rounded-xl h-full hover:border-accent/50 transition-all duration-500 relative z-10 backdrop-blur-xl shadow-lg dark:hover:shadow-[0_0_30px_rgba(255,255,255,0.05)]">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-2.5 h-2.5 bg-accent rounded-full animate-pulse"></div>
                            <span className="text-[10px] font-bold text-accent uppercase tracking-widest">Sync Replica</span>
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-1 uppercase tracking-wide">LALITPUR (AZ-2)</h3>
                        <p className="text-slate-500 dark:text-white/60 text-xs font-bold mb-3 uppercase tracking-wider">Near DR</p>
                        <ul className="space-y-1.5">
                            <ZoneBullet text="Zero Data Loss (RPO=0)" />
                            <ZoneBullet text="Synchronous Real-time Replication" />
                            <ZoneBullet text="Dark Fiber Ring Connected" />
                            <ZoneBullet text="Active-Active Load Balancing Ready" />
                            <ZoneBullet text="5km Aerial Separation from Primary" />
                        </ul>
                    </div>
                </div>

                {/* Butwal */}
                <div className="relative group">
                    {/* Connection Point */}
                    <div className="hidden md:block absolute left-[-12px] lg:left-[-16px] top-1/2 -translate-y-1/2 w-4 h-4 bg-blue-500 rounded-full shadow-[0_0_15px_#3b82f6] z-20 animate-pulse border-2 border-white dark:border-slate-900"></div>

                    <div className="bg-white dark:bg-slate-900/90 border border-slate-200 dark:border-blue-900/50 p-5 rounded-xl h-full hover:border-blue-500/50 transition-all duration-500 relative z-10 shadow-lg dark:shadow-[0_0_30px_rgba(59,130,246,0.1)] backdrop-blur-xl">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-2.5 h-2.5 bg-blue-500 rounded-full"></div>
                            <span className="text-[10px] font-bold text-blue-500 dark:text-blue-400 uppercase tracking-widest">Async Copy</span>
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-1 uppercase tracking-wide">BUTWAL (AZ-3)</h3>
                        <p className="text-blue-500 dark:text-blue-400 text-xs font-bold mb-3 uppercase tracking-wider">Far DR</p>
                        <ul className="space-y-1.5">
                            <ZoneBullet text="RTO < 15 Minutes Guaranteed" />
                            <ZoneBullet text="Distinct Tectonic/Seismic Zone" />
                            <ZoneBullet text="250km+ Geographic Separation" />
                            <ZoneBullet text="Asynchronous Data Replication" />
                            <ZoneBullet text="Air-Gapped Backup & Recovery" />
                        </ul>
                    </div>
                </div>

            </div>
        </div>
      </section>

      {/* 6. SECURITY & COMPLIANCE - UPDATED */}
      <section className="py-24 bg-white dark:bg-slate-950">
        <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
                <div>
                    <h2 className="text-4xl font-bold text-slate-900 dark:text-white mb-6">Security That Meets Regulatory Reality</h2>
                    <p className="text-lg text-slate-600 dark:text-slate-400 mb-8">
                        Security is foundational to our platform, not an optional add-on. We deploy a defense-in-depth strategy.
                    </p>
                    <ul className="space-y-5">
                        <SecurityPoint icon={<Key />} text="Privilege Access Management" />
                        <SecurityPoint icon={<Shield />} text="Zero Trust architecture" />
                        <SecurityPoint icon={<Lock />} text="Micro-segmentation & isolation" />
                        <SecurityPoint icon={<Activity />} text="24/7 Managed SOC" />
                        <SecurityPoint icon={<CheckCircle2 />} text="Continuous threat detection" />
                    </ul>
                </div>
                
                {/* Updated Visual - Removed ISO/PCI Badges */}
                <div className="bg-slate-50 dark:bg-slate-900 p-10 rounded-3xl border border-slate-200 dark:border-white/10 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-accent/5 rounded-full blur-[80px]"></div>
                    
                    <div className="relative z-10 flex flex-col gap-6">
                        <div className="p-6 bg-white dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-white/5 shadow-lg flex items-center gap-4">
                            <div className="p-3 rounded-full bg-green-500/10 text-green-600"><Shield className="h-8 w-8" /></div>
                            <div>
                                <h4 className="font-bold text-slate-900 dark:text-white text-lg">Defense-in-Depth</h4>
                                <p className="text-sm text-slate-500">Multi-layered protection</p>
                            </div>
                        </div>
                        
                        <div className="p-6 bg-white dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-white/5 shadow-lg flex items-center gap-4 ml-8">
                            <div className="p-3 rounded-full bg-blue-500/10 text-blue-600"><Activity className="h-8 w-8" /></div>
                            <div>
                                <h4 className="font-bold text-slate-900 dark:text-white text-lg">Real-time Monitoring</h4>
                                <p className="text-sm text-slate-500">24/7 SOC Surveillance</p>
                            </div>
                        </div>

                        <div className="p-6 bg-white dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-white/5 shadow-lg flex items-center gap-4">
                            <div className="p-3 rounded-full bg-purple-500/10 text-purple-600"><Lock className="h-8 w-8" /></div>
                            <div>
                                <h4 className="font-bold text-slate-900 dark:text-white text-lg">Data Sovereignty</h4>
                                <p className="text-sm text-slate-500">100% Local Residency</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
      </section>

      {/* 8. WHY EVERESTCLOUD */}
      <section className="py-24 bg-white dark:bg-slate-950">
        <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white text-center mb-16">Why Leading Institutions Choose EverestCloud</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                <ReasonCard text="Built specifically for Nepal’s regulatory environment" />
                <ReasonCard text="Designed for BFSI, government, and national systems" />
                <ReasonCard text="Operated by enterprise infrastructure experts" />
                <ReasonCard text="No dependency on foreign jurisdictions" />
                <ReasonCard text="Local support, accountability, and legal recourse" />
                <ReasonCard text="Transparent, predictable pricing in NPR" />
            </div>
        </div>
      </section>

      {/* 9. FINAL CTA - UNIFIED COLOR THEME */}
      <section className="py-24 bg-white dark:bg-slate-950 border-t border-slate-200 dark:border-white/10 relative overflow-hidden transition-colors duration-500">
        {/* Background Elements */}
        <div className="absolute inset-0 bg-grid-slate-900/[0.04] dark:bg-grid-white/[0.02] bg-[size:40px_40px]"></div>
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-accent/5 blur-[120px] rounded-full pointer-events-none"></div>
        
        <div className="container mx-auto px-4 relative z-10 flex flex-col md:flex-row items-center justify-between gap-10 max-w-6xl">
            <div className="text-left md:max-w-2xl flex-1 min-w-0">
                <h2 className="text-3xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4 tracking-tight md:whitespace-nowrap">Ready to Migrate with Confidence?</h2>
                <p className="text-lg md:text-xl text-slate-600 dark:text-slate-400 font-light leading-relaxed">
                    Join Nepal’s leading banks and enterprises securing their mission-critical infrastructure on EverestCloud.
                </p>
            </div>
            <Button 
                size="xl" 
                className="h-16 px-10 text-lg font-bold bg-accent text-accent-foreground hover:bg-accent/90 shadow-[0_0_30px_rgba(234,179,8,0.2)] rounded-xl whitespace-nowrap transition-transform hover:scale-105"
                onClick={() => openModal('architecture_review')}
            >
                Request Architecture Review
            </Button>
        </div>
      </section>
    </Layout>
  );
};

// Component Helpers
const HeroBullet = ({ text }) => (
    <div className="flex items-center gap-3 text-slate-700 dark:text-slate-200 bg-white/70 dark:bg-black/20 px-4 py-2 rounded-lg backdrop-blur-sm border border-slate-200/50 dark:border-white/5 shadow-sm">
        <div className="h-1.5 w-1.5 rounded-full bg-accent shrink-0 shadow-[0_0_5px_var(--accent)]"></div>
        <span className="text-base font-medium">{text}</span>
    </div>
);

const SectorCard = ({ icon, title, desc }) => (
    <div className="p-6 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 hover:border-accent/50 transition-colors group">
        <div className="mb-4 p-3 bg-white dark:bg-slate-900 rounded-lg w-fit text-accent shadow-sm">{icon}</div>
        <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2 group-hover:text-accent transition-colors">{title}</h3>
        <p className="text-slate-600 dark:text-slate-400 text-sm">{desc}</p>
    </div>
);

const RiskSolution = ({ risk, solution }) => (
    <div className="flex gap-4">
        <div className="w-1 bg-red-500/50 rounded-full h-full min-h-[3rem]"></div>
        <div>
            <h4 className="text-red-500 dark:text-red-400 font-bold text-sm uppercase mb-1">Risk: {risk}</h4>
            <p className="text-slate-700 dark:text-white font-medium">{solution}</p>
        </div>
    </div>
);

const FeatureBlock = ({ icon, title, desc }) => (
    <div className="p-6 border-l-2 border-slate-200 dark:border-slate-800 hover:border-accent pl-6 transition-colors group">
        <div className="mb-4 text-slate-400 dark:text-slate-500 group-hover:text-accent transition-colors">{icon}</div>
        <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">{title}</h3>
        <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">{desc}</p>
    </div>
);

const ZoneBullet = ({ text }) => (
    <li className="flex items-start gap-3 text-slate-600 dark:text-slate-300 text-xs font-medium">
        <CheckCircle2 className="h-3.5 w-3.5 text-accent/70 shrink-0 mt-0.5" />
        <span className="leading-snug">{text}</span>
    </li>
);

const SecurityPoint = ({ icon, text }) => (
    <li className="flex items-center gap-3 text-slate-700 dark:text-slate-300">
        <div className="text-accent">{icon}</div>
        <span className="font-medium">{text}</span>
    </li>
);

const ReasonCard = ({ text }) => (
    <div className="flex items-start gap-4 p-4 rounded-lg hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
        <CheckCircle2 className="h-6 w-6 text-accent shrink-0 mt-0.5" />
        <p className="text-lg text-slate-700 dark:text-slate-300 font-light">{text}</p>
    </div>
);

export default Home;
